import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import { SEGMENTS } from "@/lib/constants";
import { Building2, Upload, Palette, ShieldCheck, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { motion } from "framer-motion";

export default function Onboarding() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", fantasy_name: "", cnpj: "", phone: "", whatsapp: "", email: "", city: "", state: "",
    address: "", number: "", cep: "", responsible: "", pix: "", logo: "", segment: "assistencia",
    default_warranty: { term: "90 dias", covered: "Serviço executado e peças substituídas.", not_covered: "Mau uso, quedas e danos por terceiros." },
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name) { toast.error("Informe o nome da empresa"); setStep(0); return; }
    setSaving(true);
    try {
      await api.post("/company", form);
      await checkAuth();
      toast.success("Empresa configurada! Vamos começar 🎉");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const steps = ["Dados da empresa", "Segmento", "Garantia padrão"];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <img src="/np-logo.png" alt="NotaHoje" className="h-9 w-9 rounded-lg object-contain bg-white border border-border p-0.5" />
            <span className="font-display font-extrabold text-lg">NotaHoje</span>
          </div>
          <ThemeToggle />
        </div>

        <div className="mb-6">
          <h1 className="font-display font-extrabold text-3xl">Olá! Vamos configurar seu negócio 👋</h1>
          <p className="text-muted-foreground mt-1">Só precisa fazer isso uma vez. Leva menos de 2 minutos.</p>
        </div>

        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-primary" : "bg-border"}`} />
              <p className={`text-xs mt-1.5 font-medium ${i <= step ? "text-primary" : "text-muted-foreground"}`}>{s}</p>
            </div>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          <Card className="p-6">
            {step === 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary font-semibold"><Building2 className="h-5 w-5" /> Dados da empresa</div>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-2xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-accent shrink-0">
                    {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-cover" /> : <Palette className="h-7 w-7 text-muted-foreground" />}
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg" onChange={uploadLogo} className="hidden" data-testid="logo-upload-input" />
                    <span className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-border hover:bg-accent transition-colors text-sm font-medium"><Upload className="h-4 w-4" /> Enviar logo (PNG/JPG)</span>
                  </label>
                </div>
                <div><Label>Nome da empresa *</Label><Input value={form.name} onChange={set("name")} className="mt-1 h-11" placeholder="Ex: Refrigelar Assistência" data-testid="company-name-input" /></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Nome fantasia</Label><Input value={form.fantasy_name} onChange={set("fantasy_name")} className="mt-1 h-11" data-testid="company-fantasy-input" /></div>
                  <div><Label>CPF / CNPJ</Label><Input value={form.cnpj} onChange={set("cnpj")} className="mt-1 h-11" data-testid="company-cnpj-input" /></div>
                  <div><Label>Telefone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1 h-11" data-testid="company-phone-input" /></div>
                  <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={set("whatsapp")} className="mt-1 h-11" placeholder="Ex: 5511999999999" data-testid="company-whatsapp-input" /></div>
                  <div><Label>Cidade</Label><Input value={form.city} onChange={set("city")} className="mt-1 h-11" data-testid="company-city-input" /></div>
                  <div><Label>Estado</Label><Input value={form.state} onChange={set("state")} className="mt-1 h-11" data-testid="company-state-input" /></div>
                  <div><Label>Responsável</Label><Input value={form.responsible} onChange={set("responsible")} className="mt-1 h-11" data-testid="company-responsible-input" /></div>
                  <div><Label>PIX (opcional)</Label><Input value={form.pix} onChange={set("pix")} className="mt-1 h-11" data-testid="company-pix-input" /></div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary font-semibold"><Building2 className="h-5 w-5" /> Qual seu segmento?</div>
                <p className="text-sm text-muted-foreground">Assim personalizamos os campos dos seus documentos.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {SEGMENTS.map((s) => (
                    <button key={s.id} onClick={() => setForm({ ...form, segment: s.id })} data-testid={`segment-${s.id}`}
                      className={`p-3 rounded-xl border text-sm font-medium text-left transition-all active:scale-95 ${form.segment === s.id ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary font-semibold"><ShieldCheck className="h-5 w-5" /> Garantia padrão</div>
                <p className="text-sm text-muted-foreground">Aparecerá automaticamente nos documentos (você pode alterar em cada um).</p>
                <div><Label>Prazo de garantia</Label><Input value={form.default_warranty.term} onChange={(e) => setForm({ ...form, default_warranty: { ...form.default_warranty, term: e.target.value } })} className="mt-1 h-11" data-testid="warranty-term-input" /></div>
                <div><Label>O que está coberto</Label><Textarea value={form.default_warranty.covered} onChange={(e) => setForm({ ...form, default_warranty: { ...form.default_warranty, covered: e.target.value } })} className="mt-1" data-testid="warranty-covered-input" /></div>
                <div><Label>O que não está coberto</Label><Textarea value={form.default_warranty.not_covered} onChange={(e) => setForm({ ...form, default_warranty: { ...form.default_warranty, not_covered: e.target.value } })} className="mt-1" data-testid="warranty-notcovered-input" /></div>
              </div>
            )}
          </Card>
        </motion.div>

        <div className="flex justify-between mt-6">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button>
          {step < 2 ? (
            <Button onClick={() => { if (step === 0 && !form.name) { toast.error("Informe o nome"); return; } setStep(step + 1); }} data-testid="onboarding-next-btn">Continuar <ArrowRight className="h-4 w-4 ml-1" /></Button>
          ) : (
            <Button onClick={save} disabled={saving} data-testid="onboarding-finish-btn"><Check className="h-4 w-4 mr-1" /> {saving ? "Salvando..." : "Concluir"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
